import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AuthShell, AuthError, AuthSuccess } from '../components/auth/AuthShell';
import { useI18n } from '../contexts/I18nContext';
import * as authApi from '../lib/authApi';

/** Mirrors the backend isStrongPassword rule: ≥8 chars, an uppercase and a digit. */
export function isStrongPassword(password: string): boolean {
  return password.length >= 8 && /[A-Z]/.test(password) && /\d/.test(password);
}

export function ResetPasswordPage() {
  const { token = '' } = useParams<{ token: string }>();
  const { t } = useI18n();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t('auth.reset.mismatch'));
      return;
    }
    if (!isStrongPassword(password)) {
      setError(t('auth.reset.weak'));
      return;
    }
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.reset.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title={t('auth.reset.title')}>
      {done ? (
        <>
          <AuthSuccess message={t('auth.reset.success')} />
          <p className="text-center text-sm" style={{ color: '#64748B' }}>
            <Link to="/login" style={{ color: '#A78BFA' }} className="underline">
              {t('auth.reset.goToLogin')}
            </Link>
          </p>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <AuthError message={error} />}

          <label className="block text-sm">
            <span style={{ color: '#94A3B8' }}>{t('auth.reset.password')}</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-field mt-1 w-full px-3 py-2 outline-none"
            />
          </label>

          <label className="block text-sm">
            <span style={{ color: '#94A3B8' }}>{t('auth.reset.confirm')}</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="login-field mt-1 w-full px-3 py-2 outline-none"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="login-submit w-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? t('auth.reset.submitting') : t('auth.reset.submit')}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
